<template>
  <div class="fields-table">
    <Button
      v-if="!disabled"
      class="top-create--btn action-add-btn"
      icon="md-add"
      type="primary"
      ghost
      :disabled="onlyOneField"
      @click="showModal"
    >
      {{ $t('common.add') }}
    </Button>
    <Table row-key="id" :columns="structCol" :data="data">
      <template v-if="row.name !== '' && !disabled" slot-scope="{ row, index }" slot="action">
        <Button
          v-if="row.type === 'struct'"
          shape="circle"
          type="primary"
          size="small"
          ghost
          @click="showModal(row)"
          style="margin-right: 8px"
        >
          {{ $t('common.add') }}
        </Button>
        <Button shape="circle" type="error" size="small" ghost @click="removeField(row, index)">
          {{ $t('common.delete') }}
        </Button>
      </template>
    </Table>
    <Modal
      v-model="viewModalVisible"
      class-name="action-modal--view view-form"
      :title="$t('streams.addField')"
      :width="650"
      :mask-closable="false"
      @on-visible-change="handleVisibleChange"
    >
      <Form v-if="viewModalVisible" ref="fieldForm" :model="fieldForm" :rules="fieldRule">
        <Row :gutter="20">
          <i-col span="12">
            <FormItem :label="$t('common.name')" prop="name">
              <i-input v-model="fieldForm.name" size="large"></i-input>
            </FormItem>
          </i-col>
          <i-col span="12">
            <FormItem :label="$t('common.type')" prop="type">
              <Select v-model="fieldForm.type" size="large">
                <Option v-for="(value, index) in typeList" :value="value" :key="index">
                  {{ value }}
                </Option>
              </Select>
            </FormItem>
          </i-col>
          <i-col v-if="fieldForm.type === 'array'" span="12">
            <FormItem :label="$t('streams.arrayType')" prop="arrayType">
              <Select v-model="fieldForm.arrayType" size="large">
                <Option v-for="(value, index) in arrayTypeList" :value="value" :key="index">
                  {{ value }}
                </Option>
              </Select>
            </FormItem>
          </i-col>
        </Row>
      </Form>
      <div slot="footer">
        <Button type="text" @click="viewModalVisible = false">{{ $t('common.cancel') }}</Button>
        <Button type="primary" @click="save">{{ $t('common.submit') }}</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import genStringID from '../../../utils/genStringID'

export default {
  name: 'FieldsTable',

  model: {
    prop: 'value',
    event: 'update',
  },

  props: {
    value: {
      required: true,
    },
    isDetail: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    streamFormat: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      viewModalVisible: false,
      onlyOneField: false,
      fieldForm: {},
      fieldRule: {
        name: [{ required: true, message: this.$t('common.fillContent') }],
        type: [{ required: true, message: this.$t('common.fillContent') }],
        arrayType: [{ required: true, message: this.$t('common.fillContent') }],
      },
      structCol: [
        {
          title: this.$t('common.name'),
          key: 'name',
          tree: true,
        },
        {
          title: this.$t('common.type'),
          key: 'type',
        },
        {
          title: this.$t('common.operations'),
          slot: 'action',
          width: 250,
        },
      ],
      parentID: '',
      data: [],
      typeList: ['bigint', 'float', 'string', 'datetime', 'boolean', 'array', 'struct', 'bytea'],
      arrayTypeList: ['bigint', 'float', 'string', 'datetime', 'boolean', 'struct', 'bytea'],
    }
  },

  watch: {
    data: {
      deep: true,
      handler(val) {
        this.onlyOneField = this.streamFormat === 'binary' && val.length === 1
      },
    },
    value: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val) {
          const isNullObject = Object.keys(val).length === 0
          if (!isNullObject) {
            this.parseToTableData(val)
          }
        }
      },
    },
    streamFormat(val) {
      if (val !== 'binary') {
        this.onlyOneField = false
      } else {
        this.onlyOneField = this.data.length === 1
      }
    },
  },

  methods: {
    showModal(row) {
      if (row) {
        this.parentID = row.id
      }
      this.viewModalVisible = true
    },
    handleVisibleChange(val) {
      if (val) {
        this.fieldForm = {}
      } else {
        this.parentID = ''
      }
    },
    parseFormData(form) {
      const { name, type, arrayType } = form
      const data = {
        id: genStringID(),
        name,
        type,
      }
      if (type === 'array') {
        data.type = `array(${arrayType})`
      } else if (type === 'struct') {
        data.children = []
        data._showChildren = true
      }
      return data
    },
    findDataAction(findID, mapData, callback) {
      const find = (_data) => {
        for (let i = 0; i < _data.length; i += 1) {
          const item = _data[i]
          if (item.id === findID) {
            callback(item, _data, i)
            break
          } else if (item.children) {
            find(item.children)
          }
        }
      }
      find(mapData)
    },
    insertChildData(id, data, insert) {
      this.findDataAction(id, data, (findData) => {
        findData.children.push(insert)
      })
    },
    removeField(row) {
      this.findDataAction(row.id, this.data, (findData, formData, index) => {
        formData.splice(index, 1)
      })
      this.parseToModelValue(this.data)
    },
    save() {
      this.$refs.fieldForm.validate((valid) => {
        if (!valid) {
          return
        }
        const $data = this.parseFormData(this.fieldForm)
        if (this.parentID) {
          this.insertChildData(this.parentID, this.data, $data)
        } else {
          this.data.push($data)
        }
        this.parseToModelValue(this.data)
        this.viewModalVisible = false
      })
    },
    parseToModelValue() {
      const parseToKeyValueData = (_data) => {
        const res = {}
        _data.forEach((_item) => {
          if (_item.children) {
            res[_item.name] = parseToKeyValueData(_item.children)
          } else {
            res[_item.name] = _item.type
          }
        })
        return res
      }
      const val = parseToKeyValueData(this.data)
      this.$emit('update', val)
    },
    parseToTableData(data) {
      this.data = this.deconstructData(data)
    },
    deconstructData(_data) {
      return Object.keys(_data).map((key) => {
        const item = _data[key]
        if (typeof item !== 'string') {
          const children = this.deconstructData(item)
          return {
            id: genStringID(),
            _showChildren: true,
            name: key,
            type: 'struct',
            children,
          }
        }
        return {
          id: genStringID(),
          name: key,
          type: item,
        }
      })
    },
  },
}
</script>

<style lang="scss"></style>
